import { useState, useCallback } from 'react';
import { parseApiDate } from '../../../../lib/format';
import { calculatePercentile, calculateBMI } from '../../../../utils/pediatricPercentiles';
import { whoWeightLMS, whoHeightLMS, whoHeadCircumferenceLMS, whoBmiLMS } from '../../../../data/pediatric';
import {
  type PediatricData,
  emptyPediatricData,
} from './types';

function getAgeInMonths(birthDate: string): number {
  // Age in months drives growth percentiles and weight-based dosing, so a
  // date-only birth date must not slide back a day into the previous month.
  const birth = parseApiDate(birthDate);
  if (!birth) return 0;
  const now = new Date();
  let months = (now.getFullYear() - birth.getFullYear()) * 12 + (now.getMonth() - birth.getMonth());
  if (now.getDate() < birth.getDate()) months--;
  return Math.max(0, months);
}

function recalculateGrowth(
  growth: PediatricData['growth'],
  birthDate: string | null,
  gender: boolean | null,
): PediatricData['growth'] {
  if (!birthDate || gender === null || gender === undefined) return growth;

  const sex: 'male' | 'female' = gender ? 'male' : 'female';
  const ageMonths = getAgeInMonths(birthDate);
  const result = { ...growth };

  if (result.weight_kg !== null && result.weight_kg > 0 && result.height_cm !== null && result.height_cm > 0) {
    result.bmi = calculateBMI(result.weight_kg, result.height_cm);
  }
  if (result.weight_kg !== null && result.weight_kg > 0) {
    const wp = calculatePercentile(result.weight_kg, ageMonths, sex, whoWeightLMS);
    result.weight_percentile = wp.percentile;
  }
  if (result.height_cm !== null && result.height_cm > 0) {
    const hp = calculatePercentile(result.height_cm, ageMonths, sex, whoHeightLMS);
    result.height_percentile = hp.percentile;
  }
  if (result.head_cm !== null && result.head_cm > 0) {
    const hcp = calculatePercentile(result.head_cm, ageMonths, sex, whoHeadCircumferenceLMS);
    result.head_percentile = hcp.percentile;
  }
  if (result.bmi !== null && result.bmi > 0) {
    const bp = calculatePercentile(result.bmi, ageMonths, sex, whoBmiLMS);
    result.bmi_percentile = bp.percentile;
  }

  return result;
}

export const usePediatricForm = (
  patientBirthDate: string | null,
  patientGender: boolean | null,
) => {
  const [pediatricData, setPediatricData] = useState<PediatricData>(emptyPediatricData);

  const setPediatricField = useCallback(
    (section: keyof PediatricData, field: string, value: any) => {
      setPediatricData(prev => {
        if (section === 'perinatal' || section === 'feeding' || section === 'growth') {
          const updatedSection = { ...(prev[section] as any), [field]: value };
          const updated = { ...prev, [section]: updatedSection };

          if (section === 'growth') {
            updated.growth = recalculateGrowth(
              updatedSection as PediatricData['growth'],
              patientBirthDate,
              patientGender,
            );
          }

          return updated;
        }

        if (section === 'milestones' || section === 'vaccines') {
          return { ...prev, [section]: value as any[] };
        }

        return { ...prev, [section]: value as string };
      });
    },
    [patientBirthDate, patientGender],
  );

  const loadFromConsultation = (rawData: any, birthDate?: string | null, gender?: boolean | null) => {
    const rawPediatric = (rawData as any).pediatric;
    if (!rawPediatric) return;

    const parsed: PediatricData = {
      ...emptyPediatricData,
      ...rawPediatric,
      perinatal: { ...emptyPediatricData.perinatal, ...(rawPediatric.perinatal || {}) },
      feeding: { ...emptyPediatricData.feeding, ...(rawPediatric.feeding || {}) },
      growth: { ...emptyPediatricData.growth, ...(rawPediatric.growth || {}) },
      milestones: rawPediatric.milestones || [],
      vaccines: rawPediatric.vaccines || [],
    };

    const bd = birthDate ?? patientBirthDate;
    const g = gender ?? patientGender;
    if (bd && g !== null) {
      parsed.growth = recalculateGrowth(parsed.growth, bd, g);
    }

    setPediatricData(parsed);
  };

  const getPayloadData = (): PediatricData | null => {
    const hasData =
      Object.values(pediatricData.perinatal).some(v => v !== null && v !== '') ||
      Object.values(pediatricData.feeding).some(v => v !== null && v !== '') ||
      Object.values(pediatricData.growth).some(v => v !== null && v !== '') ||
      pediatricData.milestones.length > 0 ||
      pediatricData.milestones_observations !== '' ||
      pediatricData.vaccines.length > 0 ||
      pediatricData.vaccination_status !== '' ||
      pediatricData.vaccination_observations !== '';
    return hasData ? pediatricData : null;
  };

  return {
    pediatricData,
    setPediatricData,
    setPediatricField,
    loadFromConsultation,
    getPayloadData,
  };
};
